// Cosmetic, account-local acquired bytes only. Never store request URLs or credentials.
export const BRANDING_CACHE_LIMITS = { assetBytes: 2 * 1024 * 1024, totalBytes: 8 * 1024 * 1024, owners: 4 };
const DB = 'katafit-dojo-branding-v1';
const STORE = 'branding';
const TIMEOUT = 1500;
const id = value => typeof value === 'string' && value.length > 0 && value.length <= 256;
const digest = value => value === null || (typeof value === 'string' && /^[a-f0-9]{64}$/.test(value));
const asset = value => value === null || (value instanceof Blob && value.size > 0 && value.size <= BRANDING_CACHE_LIMITS.assetBytes && /^image\/(png|jpeg|webp|gif|avif)$/.test(value.type));
export async function sourceIdentity(source) {
  if (!source) return null;
  try {
    const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(source));
    return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, '0')).join('');
  } catch (_) { return null; }
}
function snapshot(value) {
  if (!value || !id(value.owner) || !id(value.dojoId) || !digest(value.logoSource) || !digest(value.backgroundSource)) return null;
  const logoBlob = value.logoBlob || null, backgroundBlob = value.backgroundBlob || null;
  if (!asset(logoBlob) || !asset(backgroundBlob) || (logoBlob && !value.logoSource) || (backgroundBlob && !value.backgroundSource)) return null;
  const focal = value.focalPoint;
  return { owner: value.owner, dojoId: value.dojoId,
    dojoName: typeof value.dojoName === 'string' ? value.dojoName.slice(0, 120) : '',
    accent: /^#[a-f0-9]{6}$/i.test(value.accent || '') ? value.accent : null,
    focalPoint: focal && Number.isFinite(focal.x) && Number.isFinite(focal.y) ? { x: Math.max(0, Math.min(100, focal.x)), y: Math.max(0, Math.min(100, focal.y)) } : null,
    logoSource: value.logoSource, backgroundSource: value.backgroundSource,
    logoBlob, backgroundBlob, updatedAt: Date.now() };
}
// Every operation is bounded and fail-soft. Writes are abortable so disposed account
// generations cannot commit late bytes. No cache read participates in app readiness.
function operation(mode, run, signal) {
  return new Promise(resolve => {
    let db, tx, done = false;
    const finish = value => { if (done) return; done = true; clearTimeout(timer); signal?.removeEventListener('abort', abort); db?.close(); resolve(value); };
    const abort = () => { try { tx?.abort(); } catch (_) {} finish(null); };
    const timer = setTimeout(abort, TIMEOUT);
    if (signal?.aborted) { abort(); return; }
    signal?.addEventListener('abort', abort, { once: true });
    try {
      const request = indexedDB.open(DB, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: 'owner' });
      request.onerror = () => finish(null);
      request.onblocked = () => finish(null);
      request.onsuccess = () => {
        db = request.result;
        if (done || signal?.aborted) { db.close(); return; }
        try {
          tx = db.transaction(STORE, mode);
          let result = null;
          tx.oncomplete = () => finish(result);
          tx.onerror = tx.onabort = () => finish(null);
          run(tx.objectStore(STORE), value => { result = value; }, abort);
        } catch (_) { abort(); }
      };
    } catch (_) { finish(null); }
  });
}
export function readBranding(owner, signal) {
  if (!id(owner)) return Promise.resolve(null);
  return operation('readonly', (store, result) => {
    const request = store.get(owner);
    request.onsuccess = () => { const value = snapshot(request.result); result(value?.owner === owner ? value : null); };
  }, signal);
}
export function clearBranding(owner, signal) {
  if (!id(owner)) return Promise.resolve(null);
  return operation('readwrite', store => store.delete(owner), signal);
}
export function writeBranding(value, signal) {
  const clean = snapshot(value);
  if (!clean) return Promise.resolve(null);
  return operation('readwrite', (store, result, abort) => {
    const request = store.getAll();
    request.onsuccess = () => {
      try {
        if (signal?.aborted) return;
        const rows = request.result.filter(row => row.owner !== clean.owner && snapshot(row)).sort((a, b) => b.updatedAt - a.updatedAt);
        // Several writes can share a millisecond: preserve strict refresh recency.
        clean.updatedAt = rows.reduce((latest, row) => Number.isFinite(row.updatedAt) ? Math.max(latest, row.updatedAt + 1) : latest, clean.updatedAt);
        let bytes = (clean.logoBlob?.size || 0) + (clean.backgroundBlob?.size || 0), count = 1;
        const retain = new Set([clean.owner]);
        for (const row of rows) {
          const size = (row.logoBlob?.size || 0) + (row.backgroundBlob?.size || 0);
          if (count < BRANDING_CACHE_LIMITS.owners && bytes + size <= BRANDING_CACHE_LIMITS.totalBytes) { retain.add(row.owner); bytes += size; count++; }
        }
        request.result.forEach(row => { if (!retain.has(row.owner)) store.delete(row.owner); });
        store.put(clean);
      } catch (_) { abort(); }
    };
  }, signal);
}
